import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import Swal from 'sweetalert2';
import './RapportMouvementsInventaire.css';

const API_URL = import.meta.env.VITE_API_URL;

const RapportMouvementsInventaire = ({ onNavigate, onLogout, currentPage }) => {
    const [mouvements, setMouvements] = useState([]);
    const [stats, setStats] = useState({
        retour_client: { totalQuantite: 0, count: 0 },
        defectueux: { totalQuantite: 0, count: 0 },
        perte_casse: { totalQuantite: 0, count: 0 },
        reintegration: { totalQuantite: 0, count: 0 },
        totals: { totalQuantite: 0, totalMontant: 0 },
        byProduit: [],
        byTypeProduit: []
    });
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'dateMouvement', direction: 'desc' });
    const [datePreset, setDatePreset] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterType, page, datePreset, startDate, endDate]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const dateParams = {};
            if (startDate || endDate) {
                if (startDate) dateParams.startDate = startDate;
                if (endDate) dateParams.endDate = endDate;
            } else if (datePreset) {
                dateParams.datePreset = datePreset;
            }

            const params = { page, limit: 20, ...dateParams };
            if (filterType) params.typeMouvement = filterType;

            const [mouvRes, statsRes] = await Promise.all([
                axios.get(`${API_URL}/mouvements-inventaire/mouvements`, { headers, params }),
                axios.get(`${API_URL}/mouvements-inventaire/statistiques`, { headers, params: dateParams })
            ]);

            setMouvements(mouvRes.data.mouvements);
            setTotalPages(mouvRes.data.pages);
            const statsData = statsRes.data || {};
            setStats({
                retour_client: statsData.retour_client || { totalQuantite: 0, count: 0 },
                defectueux: statsData.defectueux || { totalQuantite: 0, count: 0 },
                perte_casse: statsData.perte_casse || { totalQuantite: 0, count: 0 },
                reintegration: statsData.reintegration || { totalQuantite: 0, count: 0 },
                totals: statsData.totals || { totalQuantite: 0, totalMontant: 0 },
                byProduit: statsData.byProduit || [],
                byTypeProduit: statsData.byTypeProduit || []
            });
        } catch (error) {
            console.error('Erreur fetchData:', error);
            Swal.fire('Erreur', 'Impossible de charger les données d\'inventaire', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'retour_client': return 'Retour Client';
            case 'defectueux': return 'Défectueux';
            case 'perte_casse': return 'Perte / Casse';
            case 'reintegration': return 'Réintégration';
            default: return type;
        }
    };

    const getClientLabel = (client) => {
        if (!client) return '';
        const fullName = [client.nom, client.prenom].filter(Boolean).join(' ').trim();
        return fullName || client.raisonSociale || client.email || 'Client';
    };

    const getUserLabel = (user) => {
        if (!user) return 'Systeme';
        return [user.prenom, user.nom].filter(Boolean).join(' ').trim() || 'Systeme';
    };

    const formatMontant = (value) => Number(value || 0).toLocaleString('fr-TN', { minimumFractionDigits: 3 });
    const formatQuantite = (value) => Number(value || 0).toLocaleString('fr-TN', { maximumFractionDigits: 3 });

    const requestSort = (key) => {
        setSortConfig(prev => {
            const direction = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
            return { key, direction };
        });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '↕️';
        return sortConfig.direction === 'asc' ? '🔼' : '🔽';
    };

    const sortedMouvements = useMemo(() => {
        const result = Array.isArray(mouvements) ? [...mouvements] : [];
        const { key, direction } = sortConfig;
        const order = direction === 'asc' ? 1 : -1;

        const normalize = (value) => (typeof value === 'string' ? value.toLowerCase() : value);

        result.sort((a, b) => {
            let valA;
            let valB;

            switch (key) {
                case 'dateMouvement':
                    valA = new Date(a.dateMouvement || 0).getTime();
                    valB = new Date(b.dateMouvement || 0).getTime();
                    break;
                case 'produit':
                    valA = normalize(a.produitId?.nomProduit || '');
                    valB = normalize(b.produitId?.nomProduit || '');
                    break;
                case 'type':
                    valA = normalize(getTypeLabel(a.typeMouvement));
                    valB = normalize(getTypeLabel(b.typeMouvement));
                    break;
                case 'quantite':
                    valA = Number(a.quantite || 0);
                    valB = Number(b.quantite || 0);
                    break;
                case 'motif':
                    valA = normalize(a.motif || '');
                    valB = normalize(b.motif || '');
                    break;
                case 'utilisateur':
                    valA = normalize(getUserLabel(a.utilisateurId));
                    valB = normalize(getUserLabel(b.utilisateurId));
                    break;
                case 'client':
                    valA = normalize(a.typeMouvement === 'retour_client' ? getClientLabel(a.clientId) : '');
                    valB = normalize(b.typeMouvement === 'retour_client' ? getClientLabel(b.clientId) : '');
                    break;
                default:
                    return 0;
            }

            if (valA < valB) return -1 * order;
            if (valA > valB) return 1 * order;
            return 0;
        });

        return result;
    }, [mouvements, sortConfig]);

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
            <div className="rmi-container">
                <div className="rmi-header">
                    <h1>📦 Historique des Mouvements</h1>
                    <p>Suivi des retours, défauts et pertes de stock.</p>
                </div>

                {loading ? (
                    <LoadingSpinner message="Chargement des données..." />
                ) : (
                    <>
                        {/* KPI Cards */}
                        <div className="rmi-kpi-grid">
                            <div className="rmi-kpi-card retour">
                                <h3>Retours Clients</h3>
                                <div className="rmi-kpi-value">{stats.retour_client.totalQuantite} <span>unités</span></div>
                                <div className="rmi-kpi-desc">{stats.retour_client.count} mouvements</div>
                            </div>
                            <div className="rmi-kpi-card defectueux">
                                <h3>Produits Défectueux</h3>
                                <div className="rmi-kpi-value">{stats.defectueux.totalQuantite} <span>unités</span></div>
                                <div className="rmi-kpi-desc">{stats.defectueux.count} mouvements</div>
                            </div>
                            <div className="rmi-kpi-card perte">
                                <h3>Pertes & Casses</h3>
                                <div className="rmi-kpi-value">{stats.perte_casse.totalQuantite} <span>unités</span></div>
                                <div className="rmi-kpi-desc">{stats.perte_casse.count} mouvements</div>
                            </div>
                        </div>

                        <div className="rmi-summary-grid">
                            <div className="rmi-summary-card">
                                <h3>Total quantité</h3>
                                <div className="rmi-summary-value">
                                    {formatQuantite(stats.totals.totalQuantite)} <span>unités</span>
                                </div>
                            </div>
                            <div className="rmi-summary-card">
                                <h3>Montant perdu</h3>
                                <div className="rmi-summary-value">
                                    {formatMontant(stats.totals.totalMontant)} <span>DT</span>
                                </div>
                            </div>
                        </div>

                        <div className="rmi-advanced-stats">
                            <div className="rmi-stats-card">
                                <div className="rmi-stats-header">
                                    <h3>Perdu par produit</h3>
                                    <span className="rmi-stats-count">{stats.byProduit.length} produits</span>
                                </div>
                                <div className="rmi-stats-table-wrap">
                                    <table className="rmi-stats-table">
                                        <thead>
                                            <tr>
                                                <th>Produit</th>
                                                <th>Type produit</th>
                                                <th>Qté</th>
                                                <th>Montant</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stats.byProduit.length > 0 ? stats.byProduit.map((item) => (
                                                <tr key={item._id}>
                                                    <td>
                                                        <div className="rmi-produit-nom">{item.nomProduit}</div>
                                                        <div className="rmi-produit-ref">Ref: {item.reference || 'N/A'}</div>
                                                    </td>
                                                    <td>{item.typeProduitLabel || 'Non défini'}</td>
                                                    <td className="rmi-qte">{formatQuantite(item.totalQuantite)}</td>
                                                    <td className="rmi-montant">{formatMontant(item.totalMontant)} DT</td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="4" className="rmi-empty">Aucune donnée.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="rmi-stats-card">
                                <div className="rmi-stats-header">
                                    <h3>Perdu par type produit</h3>
                                    <span className="rmi-stats-count">{stats.byTypeProduit.length} types</span>
                                </div>
                                <div className="rmi-stats-table-wrap">
                                    <table className="rmi-stats-table">
                                        <thead>
                                            <tr>
                                                <th>Type produit</th>
                                                <th>Qté</th>
                                                <th>Montant</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stats.byTypeProduit.length > 0 ? stats.byTypeProduit.map((item) => (
                                                <tr key={item._id || item.typeProduitLabel}>
                                                    <td>{item.typeProduitLabel || 'Non défini'}</td>
                                                    <td className="rmi-qte">{formatQuantite(item.totalQuantite)}</td>
                                                    <td className="rmi-montant">{formatMontant(item.totalMontant)} DT</td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="3" className="rmi-empty">Aucune donnée.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Filters & Table */}
                        <div className="rmi-main-content">
                            <div className="rmi-filters">
                                <div className="rmi-filters-left">
                                    <h2>Détail des opérations</h2>
                                    <div className="rmi-date-presets">
                                        <button
                                            type="button"
                                            className={`rmi-preset-btn ${datePreset === 'jour' ? 'active' : ''}`}
                                            onClick={() => { setDatePreset('jour'); setStartDate(''); setEndDate(''); setPage(1); }}
                                        >Jour</button>
                                        <button
                                            type="button"
                                            className={`rmi-preset-btn ${datePreset === 'semaine' ? 'active' : ''}`}
                                            onClick={() => { setDatePreset('semaine'); setStartDate(''); setEndDate(''); setPage(1); }}
                                        >Semaine</button>
                                        <button
                                            type="button"
                                            className={`rmi-preset-btn ${datePreset === 'mois' ? 'active' : ''}`}
                                            onClick={() => { setDatePreset('mois'); setStartDate(''); setEndDate(''); setPage(1); }}
                                        >Mois</button>
                                        <button
                                            type="button"
                                            className={`rmi-preset-btn ${datePreset === 'annee' ? 'active' : ''}`}
                                            onClick={() => { setDatePreset('annee'); setStartDate(''); setEndDate(''); setPage(1); }}
                                        >Année</button>
                                    </div>
                                </div>
                                <div className="rmi-filters-right">
                                    <div className="rmi-date-range">
                                        <input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => { setDatePreset(''); setStartDate(e.target.value); setPage(1); }}
                                        />
                                        <span className="rmi-date-sep">→</span>
                                        <input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => { setDatePreset(''); setEndDate(e.target.value); setPage(1); }}
                                        />
                                        <button
                                            type="button"
                                            className="rmi-btn-clear"
                                            onClick={() => { setDatePreset(''); setStartDate(''); setEndDate(''); setPage(1); }}
                                        >Réinitialiser</button>
                                    </div>
                                    <select 
                                        className="rmi-select"
                                        value={filterType} 
                                        onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
                                    >
                                        <option value="">Tous les types</option>
                                        <option value="retour_client">Retour Client</option>
                                        <option value="defectueux">Produit Défectueux</option>
                                        <option value="perte_casse">Perte / Casse</option>
                                        <option value="reintegration">Réintégration</option>
                                    </select>
                                </div>
                            </div>

                            <div className="rmi-table-container">
                                <table className="rmi-table">
                                    <thead>
                                        <tr>
                                            <th onClick={() => requestSort('dateMouvement')} className="rmi-th-sortable">Date {getSortIcon('dateMouvement')}</th>
                                            <th onClick={() => requestSort('produit')} className="rmi-th-sortable">Produit {getSortIcon('produit')}</th>
                                            <th onClick={() => requestSort('type')} className="rmi-th-sortable">Type {getSortIcon('type')}</th>
                                            <th onClick={() => requestSort('client')} className="rmi-th-sortable">Client {getSortIcon('client')}</th>
                                            <th onClick={() => requestSort('quantite')} className="rmi-th-sortable">Qté {getSortIcon('quantite')}</th>
                                            <th onClick={() => requestSort('motif')} className="rmi-th-sortable">Motif {getSortIcon('motif')}</th>
                                            <th onClick={() => requestSort('utilisateur')} className="rmi-th-sortable">Utilisateur {getSortIcon('utilisateur')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedMouvements.length > 0 ? sortedMouvements.map((mouv) => (
                                            <tr key={mouv._id}>
                                                <td>
                                                    {new Date(mouv.dateMouvement).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
                                                </td>
                                                <td>
                                                    <div className="rmi-produit-nom">{mouv.produitId ? mouv.produitId.nomProduit : 'Produit supprimé'}</div>
                                                    <div className="rmi-produit-ref">Ref: {mouv.produitId?.reference || 'N/A'}</div>
                                                </td>
                                                <td>
                                                    <span className={`rmi-badge ${mouv.typeMouvement}`}>
                                                        {getTypeLabel(mouv.typeMouvement)}
                                                    </span>
                                                </td>
                                                <td>
                                                    {mouv.typeMouvement === 'retour_client'
                                                        ? (getClientLabel(mouv.clientId) || 'Client')
                                                        : '—'}
                                                </td>
                                                <td className="rmi-qte">
                                                    {mouv.quantite}
                                                </td>
                                                <td className="rmi-motif">
                                                    {mouv.motif}
                                                </td>
                                                <td>
                                                    <div className="rmi-user">
                                                        <div className="rmi-user-avatar">
                                                            {mouv.utilisateurId ? mouv.utilisateurId.prenom.charAt(0).toUpperCase() : 'S'}
                                                        </div>
                                                        <span>{getUserLabel(mouv.utilisateurId)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="7" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                                                    Aucun mouvement trouvé.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="rmi-pagination">
                                    <button 
                                        className="rmi-page-btn"
                                        disabled={page === 1} 
                                        onClick={() => setPage(p => p - 1)}
                                    >Précédent</button>
                                    <span className="rmi-page-info">Page {page} sur {totalPages}</span>
                                    <button 
                                        className="rmi-page-btn"
                                        disabled={page === totalPages} 
                                        onClick={() => setPage(p => p + 1)}
                                    >Suivant</button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RapportMouvementsInventaire;
